import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { useLoading } from '@/contextProviders/loadingProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';

export const usePutQuery = <BodyData, ResponseData = BodyData>(hideLoader = false) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setLoading } = useLoading();
  const { fetchWithAuth } = useFetchWithAuth();

  const put = useCallback(
    async (url: string, data?: BodyData): Promise<ResponseData | null> => {
      try {
        setErrorMessage(null);
        hideLoader ? setLoading(false) : setLoading(true);
        const response = await fetchWithAuth(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `PUT ${url} failed with status ${response.status}`);
        }

        return (await response.json()) as ResponseData;
      } catch (error: unknown | Error) {
        if (error instanceof Error) {
          const localErrorMessage = error.message;
          setErrorMessage(localErrorMessage);
          console.error(localErrorMessage);
          toast.error(localErrorMessage);
        } else {
          console.error(error);
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth, hideLoader, setLoading],
  );

  const clearError = useCallback(() => setErrorMessage(null), []);

  return { put, errorMessage, clearError };
};
